import {useCallback, useEffect, useState} from 'react'
import {BASE_URL} from '../../../api'
import config from '../../config'
const {axios} = config

// Roadmap #20 (2026-09-24): client reviews of practitioners. They go live
// immediately; practitioners can report one; hide anything here.
const Stars = ({n}) => (
  <span style={{color: '#C9A227', letterSpacing: 1}}>
    {'★'.repeat(n)}
    <span style={{color: '#ddd'}}>{'★'.repeat(5 - n)}</span>
  </span>
)

const Reviews = () => {
  const [rows, setRows] = useState([])
  const [status, setStatus] = useState('reported')
  const [loading, setLoading] = useState(false)
  const [busyId, setBusyId] = useState(null)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const {data} = await axios.get(`${BASE_URL}/admin/reviews`, {params: {status}})
      setRows(data.data || [])
    } catch (e) {
      setError('Could not load reviews.')
    } finally {
      setLoading(false)
    }
  }, [status])

  useEffect(() => {
    load()
  }, [load])

  const act = async (r, action) => {
    if (action === 'hide' && !window.confirm(`Hide this ${r.rating}-star review of ${r.practitioner?.name}?`)) return
    setBusyId(r._id)
    try {
      await axios.put(`${BASE_URL}/admin/reviews/${r._id}/${action}`)
      await load()
    } catch (e) {
      setError('Could not update this review.')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className='card mb-5 mb-xl-10'>
      <div className='card-header border-0 pt-6'>
        <div className='card-title m-0'>
          <h3 className='fw-bolder m-0'>Reviews</h3>
        </div>
        <div className='card-toolbar'>
          <select className='form-select form-select-solid w-200px' value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value='reported'>Reported</option>
            <option value='visible'>Visible</option>
            <option value='hidden'>Hidden</option>
            <option value='all'>All</option>
          </select>
        </div>
      </div>
      <div className='card-body border-top p-9'>
        <p className='text-muted'>
          Clients who've had a class or service with a practitioner can review them, and reviews go
          live immediately. Practitioners can report a review; hide anything that is fake, abusive or
          off-topic. Hiding removes it from the profile and the star average.
        </p>
        {error && <div className='alert alert-danger py-3'>{error}</div>}
        <div className='table-responsive'>
          <table className='table align-middle table-row-dashed fs-6 gy-4'>
            <thead>
              <tr className='text-start text-muted fw-bolder fs-7 text-uppercase'>
                <th>Review</th>
                <th>Practitioner</th>
                <th>Client</th>
                <th>Date</th>
                <th>Status</th>
                <th className='text-end'></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className='text-muted'>Loading...</td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={6} className='text-muted'>No reviews here.</td>
                </tr>
              )}
              {!loading &&
                rows.map((r) => (
                  <tr key={r._id}>
                    <td style={{maxWidth: 420}}>
                      <Stars n={r.rating} />
                      {r.text && <div className='mt-1'>{r.text}</div>}
                      {r.reply && <div className='text-muted fs-7 mt-1'>Reply: {r.reply}</div>}
                      {r.reported && (
                        <div className='text-danger fs-7 mt-1'>
                          Reported {new Date(r.reported.at).toLocaleDateString()}
                          {r.reported.reason ? `: ${r.reported.reason}` : ''}
                        </div>
                      )}
                    </td>
                    <td>
                      <div>{r.practitioner?.name}</div>
                      <div className='text-muted fs-7'>{r.practitioner?.town}</div>
                    </td>
                    <td>
                      <div>{r.client?.name}</div>
                      <div className='text-muted fs-7'>{r.client?.email}</div>
                    </td>
                    <td>{new Date(r.createdAt).toLocaleDateString()}</td>
                    <td>
                      <span className={`badge badge-light-${r.hidden ? 'danger' : 'success'}`}>{r.hidden ? 'Hidden' : 'Visible'}</span>
                    </td>
                    <td className='text-end'>
                      {r.hidden ? (
                        <button className='btn btn-sm btn-light' disabled={busyId === r._id} onClick={() => act(r, 'unhide')}>
                          Show again
                        </button>
                      ) : (
                        <button className='btn btn-sm btn-light-danger' disabled={busyId === r._id} onClick={() => act(r, 'hide')}>
                          Hide
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default Reviews
